import { useRef } from 'react';

import { t } from 'i18next';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { useDispatch } from 'react-redux';

import { useAppSelector } from '@/app/hooks';
import { ChuteSchedulerActions } from '@/app/slices/chuteScheduler';
import { ChuteScheduleRow } from '@/rtk-query.gql';

type Props = { className?: string };

const MINUTES_IN_DAY = 24 * 60;

const addInterval = (start: number, minutes: number): string => {
  const total = Math.floor(start / 100) * 60 + (start % 100) + minutes;
  const hh = Math.floor(total / 60) % 24;
  const mm = total % 60;
  return `${hh}${mm.toString().padStart(2, '0')}`.padStart(4, '0');
};

const overflowWithSequence = (rows: number, start: number, interval: number) =>
  Math.floor(start / 100) * 60 + (start % 100) + interval * (rows - 1) >=
  MINUTES_IN_DAY;

const overflowWithChutes = (
  rows: number,
  chuteSize: number,
  start: number,
  interval: number,
) => {
  const chutes = Math.ceil(rows / chuteSize);
  return (
    Math.floor(start / 100) * 60 + (start % 100) + interval * (chutes - 1) >=
    MINUTES_IN_DAY
  );
};

const buildTimeMap = (
  ids: string[],
  start: number,
  interval: number,
  chuteSize?: number,
) => {
  const m = new Map<string, string>();
  if (!chuteSize || chuteSize <= 1) {
    ids.forEach((id, idx) => m.set(id, addInterval(start, interval * idx)));
  } else {
    ids.forEach((id, idx) => {
      const chuteIdx = Math.floor(idx / chuteSize);
      m.set(id, addInterval(start, interval * chuteIdx));
    });
  }
  return m;
};

const sanitizeRows = (
  rows: readonly (ChuteScheduleRow | null | undefined)[],
): ChuteScheduleRow[] =>
  rows.filter((r): r is ChuteScheduleRow => !!r && !!r.aspirationId);

export const ApplyButton = ({ className }: Props) => {
  const toastRef = useRef<Toast | null>(null);
  const dispatch = useDispatch();

  const {
    schedulingRows,
    selectedSchedulingRows,
    startTime,
    interval,
    multiChuteCount,
  } = useAppSelector((s) => s.chuteScheduler);

  const hasSelection = selectedSchedulingRows.some(Boolean);
  const baseRows = hasSelection ? selectedSchedulingRows : schedulingRows;

  const target = sanitizeRows(baseRows);

  const ids = target.map((r) => r.aspirationId!);
  const startNum = Number(startTime);

  const spills =
    multiChuteCount > 1
      ? overflowWithChutes(target.length, multiChuteCount, startNum, interval)
      : overflowWithSequence(target.length, startNum, interval);

  const persistAndResequence = (map: Map<string, string>) => {
    const updated = sanitizeRows(schedulingRows).map((r) => ({
      ...r,
      aspTime: map.get(r.aspirationId) ?? r.aspTime,
    }));

    updated.sort((a, b) => {
      const aNum = a.aspTime ? Number(a.aspTime) : Infinity;
      const bNum = b.aspTime ? Number(b.aspTime) : Infinity;
      return aNum - bNum;
    });

    const resequenced = updated.map((r, idx) => ({
      ...r,
      donor: idx + 1,
      rowNum: idx + 1,
    }));

    dispatch({
      type: ChuteSchedulerActions.UPDATE_SCHEDULING_ROWS,
      rows: resequenced,
    });

    const selectedIds = new Set(
      sanitizeRows(selectedSchedulingRows).map((r) => r.aspirationId),
    );
    dispatch({
      type: ChuteSchedulerActions.UPDATE_SELECTED_SCHEDULING_ROWS,
      rows: resequenced.filter((r) => selectedIds.has(r.aspirationId)),
    });
  };

  const onClick = () => {
    if (spills) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Schedule exceeds one day',
        detail:
          'Increase the number of chutes or lower the interval, then try again.',
        life: 6000,
      });
      return;
    }

    const map = buildTimeMap(ids, startNum, interval, multiChuteCount);
    persistAndResequence(map);
  };

  const allCnt = sanitizeRows(schedulingRows).length;
  const selCnt = sanitizeRows(selectedSchedulingRows).length;

  return (
    <>
      <Toast ref={toastRef} position="bottom-center" />
      <Button
        className={className}
        icon="pi pi-clock"
        severity={selCnt === allCnt ? 'success' : 'warning'}
        label={
          selCnt === allCnt
            ? t('chuteScheduler.applyToAllRows')
            : t('chuteScheduler.applyToSelectedRows')
        }
        onClick={onClick}
      />
    </>
  );
};
